// Port of the user-supplied command_hub_no_ramp.html into Base44.
// Intentional ONE-OFF visual scale exception, requested by owner on 2026-11-02.
// Never rescale other EM²c assets or change the one-hex gameplay footprint.
// The original model spans ~14 world units, adapted here at 0.38x to sit
// inside a single flat-top radius-3 hex. Connectors are not modified.
import React, { useMemo, useEffect } from "react";
import * as THREE from "three";

const FIT_ONE_HEX = 0.38;

function createHub() {
  const group = new THREE.Group();
  const M = (color, roughness, metalness, extra = {}) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness, ...extra });
  const white = M(0xe9ebee, 0.55, 0.25);
  const dark = M(0x2b2e33, 0.6, 0.5);
  const grey = M(0xb8bcc2, 0.5, 0.4);
  const orange = M(0xff7a00, 0.5, 0.2);
  const glow = M(0xffb050, 0.4, 0, { emissive: 0xff8a1a, emissiveIntensity: 1.0 });
  const glass = M(0x10222b, 0.1, 0.8, { emissive: 0x3a1c08, emissiveIntensity: 0.7 });
  const B = (w, h, d) => new THREE.BoxGeometry(w, h, d);
  const add = (geo, mat, parent = group) => {
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true;
    m.receiveShadow = true;
    parent.add(m);
    return m;
  };
  const hex = (r0, r1, h, material, y) => {
    const mesh = add(new THREE.CylinderGeometry(r1, r0, h, 6), material);
    mesh.rotation.y = Math.PI / 6;
    mesh.position.y = y + h / 2;
    return mesh;
  };
  const section = (y0, R0, R1, h) => {
    const a0 = R0 * 0.866, a1 = R1 * 0.866;
    const tilt = Math.atan((a0 - a1) / h);
    const groups = Array.from({ length: 6 }, (_, k) => {
      const g = new THREE.Group();
      g.rotation.y = k * Math.PI / 3;
      group.add(g);
      return g;
    });
    return { y0, h, a0, a1, tilt, groups };
  };
  const put = (sec, index, geo, mat, u, t, offset = 0) => {
    const m = add(geo, mat, sec.groups[index]);
    m.position.set(u, sec.y0 + t * sec.h, sec.a0 + (sec.a1 - sec.a0) * t + offset);
    m.rotation.x = -sec.tilt;
    return m;
  };

  // Hex plinth and fixed-size illuminated inset trims from supplied model.
  hex(7, 6.6, 0.9, dark, 0);
  const S0 = section(0, 7, 6.6, 0.9);
  for (let k = 0; k < 6; k++) {
    for (const u of [-2.5, 2.5]) put(S0, k, B(1.2, 0.1, 0.08), glow, u, 0.4, 0.04);
  }

  // No ramp: preserve the original short doorway landing and deck.
  const landing = add(B(2.6, 0.9, 1.35), dark);
  landing.position.set(0, 0.45, (6.2 + 4.85) / 2);
  const gridCanvas = document.createElement("canvas");
  gridCanvas.width = gridCanvas.height = 128;
  const gc = gridCanvas.getContext("2d");
  if (gc) {
    gc.fillStyle = "#202327"; gc.fillRect(0, 0, 128, 128);
    gc.strokeStyle = "#4a4f57"; gc.lineWidth = 2;
    for (let i = 0; i <= 128; i += 8) {
      gc.beginPath(); gc.moveTo(i, 0); gc.lineTo(i, 128);
      gc.moveTo(0, i); gc.lineTo(128, i); gc.stroke();
    }
  }
  const deckTexture = new THREE.CanvasTexture(gridCanvas);
  deckTexture.wrapS = deckTexture.wrapT = THREE.RepeatWrapping;
  deckTexture.repeat.set(4, 2);
  const deck = add(new THREE.PlaneGeometry(1.9, 1.1),
    new THREE.MeshStandardMaterial({ map: deckTexture, roughness: 0.6, metalness: 0.4 }));
  deck.rotation.x = -Math.PI / 2;
  deck.position.set(0, 0.905, 5.45);
  for (const x of [-1.1, 1.1]) {
    const rail = add(B(0.12, 0.02, 1.35), orange);
    rail.position.set(x, 0.91, (6.2 + 4.85) / 2);
  }

  // Lower hexagonal hull and window panels.
  hex(5.8, 4.4, 3, white, 0.9);
  const S1 = section(0.9, 5.8, 4.4, 3);
  for (let k = 0; k < 6; k++) {
    if (k) put(S1, k, B(3.2, 0.22, 0.12), orange, 0, 0.05, 0.06);
    for (const u of [-2.5, 2.5]) put(S1, k, B(0.05, 2.7, 0.06), dark, u, 0.5, 0.03);
    put(S1, k, B(3.4, 0.1, 0.1), dark, 0, 0.99, 0.05);
  }
  for (const k of [1, 5]) {
    put(S1, k, B(2.3, 1.2, 0.08), dark, 0, 0.5, 0.04);
    put(S1, k, B(2, 0.95, 0.1), glass, 0, 0.5, 0.06);
  }
  for (const u of [-1.75, 1.75]) put(S1, 0, B(0.12, 0.7, 0.06), dark, u, 0.78, 0.04);

  // Chamfered entry, door frame, twin sliding door panels and light bar.
  const chamfer = (w, h, c, x, y) => {
    const shape = new THREE.Shape(), a = w / 2;
    shape.moveTo(x - a + c, y); shape.lineTo(x + a - c, y);
    shape.lineTo(x + a, y + c); shape.lineTo(x + a, y + h - c);
    shape.lineTo(x + a - c, y + h); shape.lineTo(x - a + c, y + h);
    shape.lineTo(x - a, y + h - c); shape.lineTo(x - a, y + c);
    shape.closePath(); return shape;
  };
  const doorFrame = chamfer(3, 2.2, 0.4, 0, 0.9);
  doorFrame.holes.push(new THREE.Path(chamfer(2.3, 1.95, 0.3, 0, 0.98).getPoints()));
  const doorFace = (shape, depth, mat, z) => {
    const m = add(new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false }), mat);
    m.position.z = z;
    return m;
  };
  doorFace(doorFrame, 1.4, dark, 3.6);
  doorFace(chamfer(2.3, 1.95, 0.3, 0, 0.98), 0.05, dark, 4.6);
  doorFace(chamfer(1.9, 1.72, 0.22, 0, 0.98), 0.12, grey, 4.68);
  for (const [w,h,d,mat,x,y,z] of [
    [0.05,1.5,0.05,dark,0,1.85,4.82],
    [1.5,0.07,0.05,glow,0,2.82,4.84],
    [0.06,0.8,0.05,glow,-1.05,1.95,4.84],
    [0.06,0.8,0.05,glow,1.05,1.95,4.84],
  ]) { const m = add(B(w,h,d),mat); m.position.set(x,y,z); }

  // Upper command deck with panoramic glass.
  hex(4.4, 4.2, 0.2, white, 3.92);
  hex(3.9, 4.9, 1.9, dark, 3.9);
  const S2 = section(3.9, 3.9, 4.9, 1.9);
  for (let k = 0; k < 6; k++) {
    put(S2, k, B(3.5, 1.35, 0.1), glass, 0, 0.5, 0.06);
    for (const u of [-1.85, 1.85]) put(S2, k, B(0.1, 1.5, 0.14), dark, u, 0.5, 0.07);
  }
  for (const u of [-1.9, 1.9]) put(S2, 0, B(0.08, 0.45, 0.08), glow, u, 0.08, 0.1);

  // Roof rim, comms dish and antenna mast.
  hex(4.95, 4.55, 0.35, white, 5.8);
  const S3 = section(5.8, 4.95, 4.55, 0.35);
  hex(5.05, 5.05, 0.08, dark, 5.74);
  for (let k = 0; k < 6; k++) put(S3, k, B(3.4, 0.12, 0.06), orange, 0, 0.5, 0.03);
  hex(4.2, 3.7, 0.25, white, 6.15);
  const cylinder = (radius, height, mat, x, y, z) => {
    const m = add(new THREE.CylinderGeometry(radius, radius, height, 24), mat);
    m.position.set(x, y + height / 2, z);
    return m;
  };
  const torus = (radius, thickness, mat, y) => {
    const m = add(new THREE.TorusGeometry(radius, thickness, 12, 48), mat);
    m.rotation.x = Math.PI / 2; m.position.y = y;
  };
  cylinder(1.5, 0.15, white, 0, 6.4, 0);
  torus(1.2, 0.07, glow, 6.58);
  torus(0.95, 0.1, dark, 6.6);
  cylinder(0.55, 0.9, dark, 0, 6.55, 0);
  torus(0.56, 0.05, glow, 7.15);
  cylinder(1.0, 0.07, white, 0, 7.7, 0);
  torus(1.0, 0.05, grey, 7.72);
  cylinder(0.15, 0.3, dark, 0, 7.4, 0);
  for (const [x,h,r] of [[0,2.8,0.07],[-0.75,1.9,0.06],[0.65,1.5,0.06]]) {
    cylinder(r,h,dark,x,7.4,0);
  }
  const mastTip = add(new THREE.SphereGeometry(0.07, 12, 12), glow);
  mastTip.position.set(0, 10.25, 0);
  group.scale.setScalar(FIT_ONE_HEX);
  group.userData.asset = "command_hub_no_ramp.html";
  return group;
}

export default function OneHexCommandHub({ under = false, progress = 1 }) {
  const model = useMemo(() => createHub(), []);
  useEffect(() => () => {
    const geometries = new Set(), materials = new Set(), textures = new Set();
    model.traverse((o) => {
      if (!o.isMesh) return;
      if (o.geometry) geometries.add(o.geometry);
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        if (!m) continue;
        materials.add(m);
        if (m.map) textures.add(m.map);
      }
    });
    geometries.forEach((x) => x.dispose());
    materials.forEach((x) => x.dispose());
    textures.forEach((x) => x.dispose());
  }, [model]);
  return <primitive object={model} visible={!under || progress > 0} />;
}
